import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Github, Globe, Info } from 'lucide-react';

const Deployment = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Deployment</h1>
          <p className="text-lg text-muted-foreground">
            Deploy your UIKit application to various hosting platforms.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Build for Production</h2>

          <CodePreview
            title="Build Command"
            code={`# Create production build
npm run build

# Preview the build locally
npm run preview`}
            language="bash"
          />

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              The build output is in the <code>dist/</code> folder. This contains static files ready for any hosting platform.
            </AlertDescription>
          </Alert>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Github className="w-6 h-6" />
            GitHub Pages
          </h2>

          <CodePreview
            title="Deploy to GitHub Pages"
            code={`# One-command deploy
npm run deploy:gh

# This runs: npm run build && gh-pages -d dist`}
            language="bash"
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configuration for GitHub Pages</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                If deploying to a repo subpath (e.g., <code>username.github.io/repo-name</code>),
                update <code>vite.config.ts</code>:
              </p>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg">
{`// vite.config.ts
export default defineConfig({
  base: '/repo-name/',  // Your repo name
  // ... rest of config
})`}
              </pre>
            </CardContent>
          </Card>

          <CodePreview
            title="GitHub Actions Workflow"
            code={`# .github/workflows/deploy.yml
name: Deploy to GitHub Pages

on:
  push:
    branches: [main]

jobs:
  deploy:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: Setup Node
        uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'

      - name: Install dependencies
        run: npm ci

      - name: Build
        run: npm run build

      - name: Deploy
        uses: peaceiris/actions-gh-pages@v3
        with:
          github_token: \${{ secrets.GITHUB_TOKEN }}
          publish_dir: ./dist`}
            language="yaml"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Globe className="w-6 h-6" />
            Vercel
          </h2>

          <CodePreview
            title="Deploy to Vercel"
            code={`# Install Vercel CLI
npm i -g vercel

# Deploy
vercel

# Or connect GitHub repo at vercel.com/new`}
            language="bash"
          />

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                Vercel auto-detects Vite projects. No configuration needed for most cases.
              </p>
              <p className="text-sm text-muted-foreground">
                For SPA routing, create <code>vercel.json</code>:
              </p>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg mt-2">
{`{
  "rewrites": [
    { "source": "/(.*)", "destination": "/" }
  ]
}`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Netlify</h2>

          <CodePreview
            title="Deploy to Netlify"
            code={`# Install Netlify CLI
npm i -g netlify-cli

# Deploy
netlify deploy --prod --dir=dist`}
            language="bash"
          />

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                For SPA routing, create <code>netlify.toml</code>:
              </p>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg">
{`[build]
  publish = "dist"
  command = "npm run build"

[[redirects]]
  from = "/*"
  to = "/index.html"
  status = 200`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Cloudflare Pages</h2>

          <CodePreview
            title="Deploy to Cloudflare Pages"
            code={`# Via Cloudflare dashboard:
# 1. Connect GitHub repository
# 2. Build command: npm run build
# 3. Build output directory: dist`}
            language="bash"
          />

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                For SPA routing, create <code>public/_redirects</code>:
              </p>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg">
{`/*    /index.html   200`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">UIKit Deploy Command</h2>

          <CodePreview
            title="UIKit CLI Deploy"
            code={`# Bundle and serve locally (for testing)
npm run bundle
npm run serve

# Deploy with UIKit CLI
npm run deploy`}
            language="bash"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Environment Variables</h2>

          <CodePreview
            title=".env Files"
            code={`# .env (local development)
VITE_API_URL=http://localhost:3000

# .env.production (production build)
VITE_API_URL=https://api.myapp.com

# Access in code:
const apiUrl = import.meta.env.VITE_API_URL`}
            language="bash"
          />

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              Only variables prefixed with <code>VITE_</code> are exposed to the browser.
              Never put secrets in VITE_ variables.
            </AlertDescription>
          </Alert>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Deployment Checklist</h2>

          <div className="space-y-2">
            {[
              'Set correct base path in vite.config.ts (for subdirectory deploys)',
              'Configure environment variables for production',
              'Set up SPA redirect rules for client-side routing',
              'Run npm run build locally to test for errors',
              'Test the production build with npm run preview',
              'Set up CI/CD for automatic deploys',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                  {i + 1}
                </div>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default Deployment;
